/**
 * Cashier (campus_agent) Onboarding Wizard — 4 steps.
 *
 * Cashiers can't *create* upstream entities — their job is to register
 * subscribers, recharge wallets, and renew cards. So the wizard is a guided
 * orientation rather than a create-form sequence.
 *
 *   1. Welcome
 *   2. How to register a subscriber
 *   3. How to recharge / renew
 *   4. Done
 */
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import {
  Sparkle, UserPlus, Wallet, CheckCircle, X, ArrowRight, ArrowLeft,
} from '@phosphor-icons/react';

const STEPS = [
  { n: 1, label: 'Welcome',   icon: Sparkle      },
  { n: 2, label: 'Register',  icon: UserPlus     },
  { n: 3, label: 'Recharge',  icon: Wallet       },
  { n: 4, label: 'Done',      icon: CheckCircle  },
];

export default function CashierWizard({ state, onAdvance, onSkip, onComplete, onDismiss }) {
  const step = Math.min(Math.max(state?.step || 1, 1), 4);
  const next = () => onAdvance(Math.min(step + 1, 4));
  const back = () => onAdvance(Math.max(step - 1, 1));

  return (
    <div className="fixed inset-0 z-[100] bg-[#0A2540]/55 backdrop-blur-sm flex items-stretch sm:items-center justify-center p-0 sm:p-4" data-testid="cashier-wizard">
      <div className="bg-[#FCFAF6] w-full sm:max-w-3xl sm:rounded-2xl shadow-2xl overflow-hidden flex flex-col sm:flex-row max-h-[100vh] sm:max-h-[88vh]">
        <aside className="sm:w-52 bg-[#0A2540] text-white p-5 flex sm:flex-col gap-3 sm:gap-5 shrink-0">
          <div className="hidden sm:block">
            <p className="text-[10px] uppercase tracking-[0.18em] text-[#C9A35B] font-semibold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
              Tour
            </p>
            <p className="text-lg font-bold mt-0.5" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              Cashier basics in 60 seconds
            </p>
          </div>
          <ol className="flex sm:flex-col gap-2 sm:gap-3 flex-1">
            {STEPS.map((s) => {
              const Ic = s.icon;
              const active = s.n === step;
              const done = s.n < step;
              return (
                <li key={s.n} className={`flex items-center gap-2 text-xs ${active ? 'opacity-100' : 'opacity-60'} ${done ? 'opacity-90' : ''}`} data-testid={`cashier-wizard-step-${s.n}`}>
                  <span className={`w-7 h-7 shrink-0 rounded-lg flex items-center justify-center ${
                    active ? 'bg-[#C9A35B] text-[#0A2540]' : done ? 'bg-[#C9A35B]/30 text-[#C9A35B]' : 'bg-white/10 text-white/60'
                  }`}>
                    <Ic size={14} weight="fill" />
                  </span>
                  <span className="font-semibold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                    {String(s.n).padStart(2, '0')} · {s.label}
                  </span>
                </li>
              );
            })}
          </ol>
        </aside>

        <main className="flex-1 flex flex-col min-h-0">
          <header className="flex items-center justify-between p-4 sm:p-5 border-b border-[#EDE6D8]">
            <p className="text-[10px] uppercase tracking-[0.18em] text-[#78716C] font-semibold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
              Step {step} of 4
            </p>
            <button type="button" onClick={onDismiss} className="text-xs text-[#78716C] hover:text-[#0A2540] inline-flex items-center gap-1" data-testid="cashier-wizard-pause">
              <X size={12} weight="bold" /> Pause &amp; continue later
            </button>
          </header>

          <div className="flex-1 overflow-y-auto p-5 sm:p-7">
            {step === 1 && <StepWelcome onNext={next} />}
            {step === 2 && <StepRegister onNext={next} onSkip={() => onSkip(2)} />}
            {step === 3 && <StepRecharge onNext={next} onSkip={() => onSkip(3)} />}
            {step === 4 && <StepDone onComplete={onComplete} />}
          </div>

          {step > 1 && step < 4 && (
            <footer className="border-t border-[#EDE6D8] px-5 py-3 flex items-center justify-between">
              <button type="button" onClick={back} className="text-xs text-[#78716C] hover:text-[#0A2540] inline-flex items-center gap-1 font-semibold" data-testid="cashier-wizard-back">
                <ArrowLeft size={12} weight="bold" /> Back
              </button>
              <p className="text-[10px] text-[#78716C] font-mono">
                Progress saves automatically.
              </p>
            </footer>
          )}
        </main>
      </div>
    </div>
  );
}

function StepWelcome({ onNext }) {
  const { user } = useAuth();
  return (
    <div className="max-w-xl">
      <div className="w-12 h-12 rounded-2xl bg-[#C9A35B]/20 text-[#C9A35B] flex items-center justify-center mb-4">
        <Sparkle size={22} weight="fill" />
      </div>
      <h2 className="text-2xl font-bold text-[#1A1D1C] mb-2" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
        Welcome, {(user?.fullname || user?.username || '').split(' ')[0] || 'cashier'}!
      </h2>
      <p className="text-sm text-[#78716C] leading-relaxed">
        Your Cashier app is built for the counter — fast, mobile-friendly, and focused on the three jobs you'll do every day. This short tour shows you where everything lives.
      </p>
      <ul className="mt-5 space-y-2 text-sm text-[#1A1D1C]">
        {['Register a new subscriber', 'Recharge or renew a card', 'Track your daily numbers'].map((t, i) => (
          <li key={i} className="flex items-start gap-2">
            <span className="w-5 h-5 shrink-0 rounded-full bg-[#0A2540]/5 text-[#0A2540] text-[10px] font-bold flex items-center justify-center" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{i + 1}</span>
            <span>{t}</span>
          </li>
        ))}
      </ul>
      <div className="mt-7">
        <Button onClick={onNext} data-testid="cashier-wizard-step1-next" className="px-5">
          Let's go <ArrowRight size={14} className="ml-2" weight="bold" />
        </Button>
      </div>
    </div>
  );
}

function StepRegister({ onNext, onSkip }) {
  return (
    <div className="max-w-xl">
      <div className="w-10 h-10 rounded-xl bg-[#0A2540]/10 text-[#0A2540] flex items-center justify-center mb-3">
        <UserPlus size={20} weight="fill" />
      </div>
      <h2 className="text-xl font-bold text-[#1A1D1C]" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
        Registering a new subscriber
      </h2>
      <p className="text-sm text-[#78716C] mt-1">
        From the home screen, tap <strong className="text-[#0A2540]">Register Subscriber</strong>. You'll need:
      </p>
      <ul className="mt-4 space-y-2 text-sm text-[#1A1D1C]">
        <li className="flex items-start gap-2"><span className="text-[#C9A35B] mt-0.5">•</span>The subscriber's name and mobile number</li>
        <li className="flex items-start gap-2"><span className="text-[#C9A35B] mt-0.5">•</span>A blank card to assign (scan the card UID)</li>
        <li className="flex items-start gap-2"><span className="text-[#C9A35B] mt-0.5">•</span>The subscription fee (if your campus charges one)</li>
        <li className="flex items-start gap-2"><span className="text-[#C9A35B] mt-0.5">•</span>Their category — Student, Staff, or Visitor</li>
      </ul>
      <div className="mt-5 bg-[#0A2540]/5 border border-[#0A2540]/10 rounded-lg p-3 text-xs text-[#0A2540]">
        Tip: Subscription fees and float balance update instantly on your home screen after each registration.
      </div>
      <div className="pt-5 flex items-center justify-between gap-3">
        <button type="button" onClick={onSkip} className="text-xs text-[#78716C] hover:text-[#0A2540] font-semibold" data-testid="cashier-wizard-skip-2">
          Skip
        </button>
        <Button onClick={onNext} data-testid="cashier-wizard-step2-next">
          Got it <ArrowRight size={14} className="ml-2" weight="bold" />
        </Button>
      </div>
    </div>
  );
}

function StepRecharge({ onNext, onSkip }) {
  return (
    <div className="max-w-xl">
      <div className="w-10 h-10 rounded-xl bg-[#10B981]/15 text-[#10B981] flex items-center justify-center mb-3">
        <Wallet size={20} weight="fill" />
      </div>
      <h2 className="text-xl font-bold text-[#1A1D1C]" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
        Recharging and renewing
      </h2>
      <p className="text-sm text-[#78716C] mt-1">
        Two quick flows, both reached from the home tile menu:
      </p>
      <div className="mt-4 space-y-3 text-sm">
        <div className="bg-white border border-[#EDE6D8] rounded-lg p-3">
          <p className="font-semibold text-[#0A2540]">Recharge Wallet</p>
          <p className="text-[#78716C] mt-1">Scan the subscriber's card or enter their mobile, then key the amount paid. Their wallet balance updates instantly.</p>
        </div>
        <div className="bg-white border border-[#EDE6D8] rounded-lg p-3">
          <p className="font-semibold text-[#0A2540]">Renew Card</p>
          <p className="text-[#78716C] mt-1">When a card expires, scan and tap renew. The system charges the renewal fee and extends the validity.</p>
        </div>
      </div>
      <div className="mt-5 bg-[#C9A35B]/10 border border-[#C9A35B]/30 rounded-lg p-3 text-xs text-[#1A1D1C]">
        Heads-up: Your Recharges and Float numbers on the home screen reset at midnight every day.
      </div>
      <div className="pt-5 flex items-center justify-between gap-3">
        <button type="button" onClick={onSkip} className="text-xs text-[#78716C] hover:text-[#0A2540] font-semibold" data-testid="cashier-wizard-skip-3">
          Skip
        </button>
        <Button onClick={onNext} data-testid="cashier-wizard-step3-next">
          Got it <ArrowRight size={14} className="ml-2" weight="bold" />
        </Button>
      </div>
    </div>
  );
}

function StepDone({ onComplete }) {
  const navigate = useNavigate();
  return (
    <div className="max-w-xl text-center">
      <div className="w-16 h-16 mx-auto rounded-2xl bg-[#10B981]/15 text-[#10B981] flex items-center justify-center mb-4">
        <CheckCircle size={32} weight="fill" />
      </div>
      <h2 className="text-2xl font-bold text-[#1A1D1C]" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
        You're ready for the counter! 🎉
      </h2>
      <p className="text-sm text-[#78716C] mt-2 leading-relaxed">
        Need a refresher later? Tap the question-mark icon in the top bar and choose <em>Setup Guide</em> any time.
      </p>
      <div className="mt-6">
        <Button onClick={async () => { await onComplete(); navigate('/cashier-app'); }} data-testid="cashier-wizard-finish" className="px-5">
          Open Cashier home <ArrowRight size={14} className="ml-2" weight="bold" />
        </Button>
      </div>
    </div>
  );
}
